import { useState } from "react";
import DonutMeter from "./DonutMeter";
const T = { accent:"#0078D4", green:"#107C10", greenL:"#DFF6DD", red:"#A4262C", redL:"#FDE7E9", amber:"#D83B01", amberL:"#FED9CC", border:"#E2E6F0", text:"#201F1E", textSub:"#605E5C", bg:"#F8F9FB", white:"#fff" };
function StatusBadge({ status }) {
  const pass = status === "pass";
  return <span style={{ background: pass ? T.greenL : T.redL, color: pass ? T.green : T.red, borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{pass ? "PASS" : "FAIL"}</span>;
}
function CheckRow({ label, data, fix }) {
  return (
    <div style={{ padding: "16px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{label}</span>
        <StatusBadge status={data?.status} />
      </div>
      {data?.record && <div style={{ background: T.bg, borderRadius: 6, padding: "8px 12px", fontSize: 11, fontFamily: "monospace", color: T.textSub, wordBreak: "break-all", marginBottom: 6 }}>{data.record}</div>}
      {data?.records && <div style={{ background: T.bg, borderRadius: 6, padding: "8px 12px", fontSize: 11, fontFamily: "monospace", color: T.textSub, marginBottom: 6 }}>{data.records.join(", ")}</div>}
      {data?.selector && <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>Selector: <b>{data.selector}</b></div>}
      {data?.status === "fail" && fix && <div style={{ background: T.amberL, borderRadius: 6, padding: "8px 12px", fontSize: 12, color: T.amber, marginTop: 6 }}><b>Fix:</b> {fix}</div>}
    </div>
  );
}
export default function DomainChecker({ defaultDomain = "" }) {
  const [domain, setDomain] = useState(defaultDomain);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const check = async () => {
    if (!domain) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await fetch("/api/dns/check?domain=" + encodeURIComponent(domain));
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setResult(d);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: "24px 28px" }}>
      <p style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Domain Health Checker</p>
      <p style={{ fontSize: 12, color: T.textSub, marginBottom: 20 }}>Check SPF, DKIM, DMARC and MX records for your sending domain</p>
      <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        <input value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === "Enter" && check()} placeholder="sdrreachout.com" style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 6, padding: "9px 12px", fontSize: 13 }} />
        <button onClick={check} disabled={loading} style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 6, padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Checking..." : "Check Domain"}
        </button>
      </div>
      {error && <div style={{ background: T.redL, color: T.red, borderRadius: 6, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>}
      {result && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, padding: "16px 20px", background: T.bg, borderRadius: 10 }}>
            <DonutMeter value={Math.round(((result.score||0)/4)*100)} size={84} strokeWidth={9} sublabel={`Grade ${result.grade}`} />
            <div>
              <p style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{result.domain}</p>
              <p style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>{result.score}/4 checks passed — {result.grade === "A" ? "Excellent deliverability" : result.grade === "B" ? "Good deliverability" : result.grade === "C" ? "Fair deliverability" : "Poor deliverability — fix issues below"}</p>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {["spf","dkim","dmarc","mx"].map(k => <span key={k} style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: result[k]?.status === "pass" ? T.greenL : T.redL, color: result[k]?.status === "pass" ? T.green : T.red }}>{k.toUpperCase()}</span>)}
              </div>
            </div>
          </div>
          <CheckRow label="SPF Record" data={result.spf} fix='Add a TXT record to your DNS: "v=spf1 include:amazonses.com include:_spf.google.com ~all"' />
          <CheckRow label="DKIM Record" data={result.dkim} fix="Add the DKIM TXT record provided by your email provider (Resend, Google, or Microsoft) to your DNS." />
          <CheckRow label="DMARC Record" data={result.dmarc} fix='Add a TXT record at _dmarc.yourdomain.com: "v=DMARC1; p=none; rua=mailto:dmarc@yourdomain.com"' />
          <CheckRow label="MX Records" data={result.mx} fix="Add MX records pointing to your email provider to receive bounces and replies." />
        </div>
      )}
    </div>
  );
}
